import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, Megaphone, Link as LinkIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AdminTabs } from "@/components/AdminTabs";
import { SERVICE_LABELS, ALL_SERVICE_CODES } from "@/lib/services";

const PLACEMENTS = [
  { key: "landing", label: "Homepage" },
  { key: "customer_dashboard", label: "Customer dashboard" },
  { key: "collector_dashboard", label: "Collector dashboard" },
];

const resolveSrc = (url) => (url && url.startsWith("http") ? url : url ? `${API}/files/${url}` : "");

const blank = {
  title: "", headline: "", body: "", image_url: "", link_url: "", cta_label: "Learn more",
  placements: [], active: true, start_at: "", end_at: "", target_zips: [], target_services: [],
  tier: "", price: "", run_terms: "", advertiser_name: "", advertiser_email: "", payment_status: "unpaid",
};

export default function AdminAdForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [ad, setAd] = useState(blank);
  const [zipInput, setZipInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [genning, setGenning] = useState(false);
  const [payLink, setPayLink] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/ads/${id}`).then(({ data }) => setAd({ ...blank, ...data })).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id, isEdit]);

  const f = (k) => (e) => setAd({ ...ad, [k]: e.target.value });
  const togglePlacement = (k) => setAd({ ...ad, placements: ad.placements.includes(k) ? ad.placements.filter((p) => p !== k) : [...ad.placements, k] });
  const toggleService = (c) => setAd({ ...ad, target_services: ad.target_services.includes(c) ? ad.target_services.filter((s) => s !== c) : [...ad.target_services, c] });
  const addZip = () => {
    const z = zipInput.trim();
    if (z && !ad.target_zips.includes(z)) setAd({ ...ad, target_zips: [...ad.target_zips, z] });
    setZipInput("");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/ads/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAd((a) => ({ ...a, image_url: data.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!ad.title.trim()) return toast.error("Give the ad an internal title");
    setSaving(true);
    const payload = { ...ad, price: parseFloat(ad.price) || 0, start_at: ad.start_at || null, end_at: ad.end_at || null };
    try {
      if (isEdit) await api.put(`/admin/ads/${id}`, payload);
      else await api.post("/admin/ads", payload);
      toast.success(isEdit ? "Ad updated" : "Ad created");
      navigate("/admin/ads");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const genPaymentLink = async () => {
    setGenning(true);
    try {
      const { data } = await api.post(`/admin/ads/${id}/payment-link`, { origin_url: window.location.origin });
      setPayLink(data.url);
      setAd((a) => ({ ...a, payment_status: "pending" }));
      toast.success("Payment link generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGenning(false);
    }
  };

  const src = resolveSrc(ad.image_url);

  return (
    <div className="container-x py-12" data-testid="admin-ad-form">
      <AdminTabs />
      <button onClick={() => navigate("/admin/ads")} className="text-sm text-[#5C6B64] hover:text-[#1E3F33] inline-flex items-center gap-1.5 mb-4" data-testid="admin-ad-back">
        <ArrowLeft className="w-4 h-4" /> Back to ads
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2"><Megaphone className="w-6 h-6 text-[#2D6A4F]" /> {isEdit ? "Edit ad" : "New ad"}</h1>
        <button onClick={save} disabled={saving} data-testid="admin-ad-save" className="btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save ad"}</button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="card-soft space-y-4">
            <h2 className="font-heading text-xl text-[#1E3F33]">Content</h2>
            <div><label className="label-soft">Internal title *</label><input data-testid="ad-title" className="input-soft" value={ad.title} onChange={f("title")} /></div>
            <div><label className="label-soft">Headline</label><input data-testid="ad-headline" className="input-soft" value={ad.headline} onChange={f("headline")} /></div>
            <div><label className="label-soft">Body text</label><textarea data-testid="ad-body" className="input-soft min-h-[80px]" value={ad.body} onChange={f("body")} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-soft">Click-through link</label><input data-testid="ad-link" className="input-soft" placeholder="https://…" value={ad.link_url} onChange={f("link_url")} /></div>
              <div><label className="label-soft">Button label</label><input data-testid="ad-cta" className="input-soft" value={ad.cta_label} onChange={f("cta_label")} /></div>
            </div>
          </div>

          <div className="card-soft space-y-3">
            <h2 className="font-heading text-xl text-[#1E3F33]">Image</h2>
            <div><label className="label-soft">Image URL (paste external, or upload below)</label><input data-testid="ad-image-url" className="input-soft" placeholder="https://… or upload" value={ad.image_url} onChange={f("image_url")} /></div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="ad-image-file" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary text-xs py-2 inline-flex items-center gap-2" data-testid="ad-image-upload-btn"><Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload image"}</button>
            {src && <img src={src} alt="preview" className="mt-2 rounded-xl border border-[#1E3F33]/10 max-h-40 object-cover" data-testid="ad-image-preview" />}
          </div>

          <div className="card-soft space-y-4" data-testid="ad-billing-card">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-xl text-[#1E3F33]">Billing &amp; advertiser</h2>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${ad.payment_status === "paid" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : ad.payment_status === "pending" ? "bg-[#CC5A3A]/15 text-[#CC5A3A]" : "bg-[#1E3F33]/10 text-[#5C6B64]"}`} data-testid="ad-payment-status">
                {ad.payment_status === "paid" ? "Paid" : ad.payment_status === "pending" ? "Pending payment" : "Unpaid"}
              </span>
            </div>
            <p className="text-xs text-[#8A9A86]">Ads only go live once payment is completed. Set the tier, price, and run terms, then generate a payment link to send to the advertiser.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-soft">Tier</label><input data-testid="ad-tier" className="input-soft" placeholder="e.g. Basic, Featured" value={ad.tier} onChange={f("tier")} /></div>
              <div><label className="label-soft">Price (USD)</label><input data-testid="ad-price" type="number" min="0" step="0.01" className="input-soft" placeholder="e.g. 50.00" value={ad.price} onChange={f("price")} /></div>
            </div>
            <div><label className="label-soft">Run terms</label><textarea data-testid="ad-run-terms" className="input-soft min-h-[60px]" placeholder="e.g. Homepage placement for 30 days from activation." value={ad.run_terms} onChange={f("run_terms")} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-soft">Advertiser name</label><input data-testid="ad-advertiser-name" className="input-soft" value={ad.advertiser_name} onChange={f("advertiser_name")} /></div>
              <div><label className="label-soft">Advertiser email</label><input data-testid="ad-advertiser-email" type="email" className="input-soft" placeholder="Send payment link here" value={ad.advertiser_email} onChange={f("advertiser_email")} /></div>
            </div>
            {isEdit ? (
              <div className="pt-2 border-t border-[#1E3F33]/8">
                <button type="button" onClick={genPaymentLink} disabled={genning} data-testid="ad-gen-payment-link" className="btn-accent text-sm inline-flex items-center gap-2"><LinkIcon className="w-4 h-4" /> {genning ? "Generating…" : "Generate payment link"}</button>
                <p className="text-[11px] text-[#8A9A86] mt-2">Save your latest changes first, then generate. The advertiser pays via this Stripe link; the ad activates automatically once paid.</p>
                {payLink && (
                  <div className="mt-3 flex items-center gap-2" data-testid="ad-payment-link-row">
                    <input readOnly className="input-soft text-xs flex-1" value={payLink} data-testid="ad-payment-link" />
                    <button type="button" onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Copied"); }} className="btn-secondary text-xs px-3" data-testid="ad-payment-link-copy">Copy</button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-[#8A9A86] pt-2 border-t border-[#1E3F33]/8">Save the ad first, then reopen it to generate a payment link.</p>
            )}
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="card-soft">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-[#1E3F33]">Active</h2>
              <Switch data-testid="ad-active" checked={!!ad.active} onCheckedChange={(v) => setAd({ ...ad, active: v })} />
            </div>
          </div>

          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3">Placements</h2>
            <div className="space-y-2">
              {PLACEMENTS.map((p) => (
                <label key={p.key} className="flex items-center gap-2.5 cursor-pointer text-sm text-[#1E3F33]" data-testid={`ad-placement-${p.key}`}>
                  <input type="checkbox" className="w-4 h-4 accent-[#2D6A4F]" checked={ad.placements.includes(p.key)} onChange={() => togglePlacement(p.key)} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3">Schedule</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-soft">Start</label><input data-testid="ad-start" type="datetime-local" className="input-soft" value={ad.start_at || ""} onChange={f("start_at")} /></div>
              <div><label className="label-soft">End</label><input data-testid="ad-end" type="datetime-local" className="input-soft" value={ad.end_at || ""} onChange={f("end_at")} /></div>
            </div>
            <p className="text-[11px] text-[#8A9A86] mt-2">Leave blank to run indefinitely.</p>
          </div>

          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-2">ZIP targeting</h2>
            <p className="text-[11px] text-[#8A9A86] mb-2">Leave empty to show in all areas.</p>
            <div className="flex gap-2">
              <input data-testid="ad-zip-input" className="input-soft flex-1" placeholder="e.g. 78701" value={zipInput} onChange={(e) => setZipInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addZip(); } }} inputMode="numeric" />
              <button type="button" onClick={addZip} className="btn-secondary text-xs px-3" data-testid="ad-zip-add">Add</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {ad.target_zips.map((z) => (
                <span key={z} className="inline-flex items-center gap-1 bg-[#1E3F33] text-white text-xs rounded-full px-2.5 py-1" data-testid={`ad-zip-${z}`}>
                  {z}<button type="button" onClick={() => setAd({ ...ad, target_zips: ad.target_zips.filter((x) => x !== z) })} className="hover:text-[#CC5A3A]">×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-2">Service targeting</h2>
            <p className="text-[11px] text-[#8A9A86] mb-2">Leave empty to show for all services.</p>
            <div className="grid grid-cols-1 gap-1.5 max-h-52 overflow-y-auto">
              {ALL_SERVICE_CODES.map((c) => (
                <label key={c} className="flex items-center gap-2 cursor-pointer text-xs text-[#1E3F33]" data-testid={`ad-service-${c}`}>
                  <input type="checkbox" className="w-3.5 h-3.5 accent-[#2D6A4F]" checked={ad.target_services.includes(c)} onChange={() => toggleService(c)} />
                  {SERVICE_LABELS[c]}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
